import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import get from 'lodash/get';
import TeamChip from '~/components/TeamChip/TeamChip';
import StyledComponent from '~/components/Styled/Styled';
import getTotalSegments from '~/util/getTotalSegments';
import isHomeFirst from '~/util/isHomeFirst';
import css from './LineScoreCard.scss';

const cssContext = classnames.bind(css);

/* eslint-disable camelcase */
/* eslint-disable react/prefer-stateless-function */

const getScoreString = team => get(team, 'score', null);

const getShotsString = team => get(team, 'shots', null);

const headersToIgnore = ['R', 'H', 'E', 'T'];

const getTableData = (lineScores, progress, boxScore, league, nhlShots) => {
  const data = { headers: [], home: [], away: [] };
  const homeLineScore = get(lineScores, 'home', []);

  const getGameLength =
    homeLineScore.length < getTotalSegments(league.slug)
      ? getTotalSegments(league.slug)
      : homeLineScore.length;

  const totalSegments = Math.max(getGameLength, progress.segment || 0);

  [...Array(totalSegments).keys()].forEach(segment => {
    const segmentObj = get(lineScores, `home[${segment}]`, {
      segment_string: segment + 1,
    });

    if (!nhlShots) {
      data.headers = [...data.headers, segmentObj.segment_string];

      ['home', 'away'].forEach(team => {
        const teamSegment = get(lineScores, `${team}[${segment}]`, {});

        data[team] = [...data[team], getScoreString(teamSegment)];
      });
    }

    if (nhlShots && segmentObj.segment_string !== 'SO') {
      data.headers = [...data.headers, segmentObj.segment_string];

      ['home', 'away'].forEach(team => {
        const teamSegment = get(lineScores, `${team}[${segment}]`, {});

        data[team] = [...data[team], getShotsString(teamSegment)];
      });
    }
  });

  // Total info
  if (league.sport_name === 'baseball') {
    data.headers = [...data.headers, 'R', 'H', 'E'];
    ['home', 'away'].forEach(team => {
      data[team] = [
        ...data[team],
        get(boxScore, `${team}_score_runs`, null),
        get(boxScore, `${team}_score_hits`, null),
        get(boxScore, `${team}_score_errors`, null),
      ];
    });

    return data;
  }

  const totalLabel = league.sport_name === 'soccer' ? 'F' : 'T';
  data.headers = [...data.headers, totalLabel];
  ['home', 'away'].forEach(team => {
    data[team] = [
      ...data[team],
      nhlShots
        ? get(boxScore, `team_records[${team}].shots`, null)
        : get(boxScore, `score[${team}].score`, null),
    ];
  });
  return data;
};

const renderHeader = (headers, league) => (
  <tr>
    <th className={css.teamColumn} aria-hidden="true">
      &nbsp;
    </th>

    {headers.map((header, index) => {
      let isTotalCol = false;

      if (
        (league.sport_name === 'football' ||
          league.sport_name === 'baseball' ||
          league.sport_name === 'basketball' ||
          league.sport_name === 'hockey') &&
        headersToIgnore.includes(header)
      ) {
        return null;
      }

      if (league.sport_name === 'baseball') {
        if (index === headers.length - 3) isTotalCol = true;
      } else if (index === headers.length - 1) {
        isTotalCol = true;
      }

      return (
        <th
          key={`header-${header}`}
          scope="col"
          className={cssContext(css.lineScoreColumnElement, {
            lineScoreTotalColumn: isTotalCol,
          })}
        >
          {header}
        </th>
      );
    })}
  </tr>
);

const renderTeamRecord = (
  team,
  progress,
  tableData,
  sport_name,
  event_status,
  isActiveSegment = false,
  inning = null
) =>
  tableData[team].map((result, index) => {
    if (headersToIgnore.includes(tableData.headers[index])) {
      return null;
    }

    let resultToDisplay = result;
    const activeInningId = {
      id: null,
    };

    if (result === null || isNaN(result)) {
      resultToDisplay = '';
    }

    if (
      sport_name === 'baseball' &&
      team === 'home' &&
      event_status === 'final' &&
      progress.home.length - 1 === index &&
      progress.home[progress.home.length - 1].score === null
    ) {
      resultToDisplay = 'X';
    }

    if (sport_name === 'baseball' && inning === index + 1 && event_status === 'in_progress') {
      activeInningId.id = 'activeInning';
    }

    const activeBaseballSegmentClass =
      isActiveSegment && sport_name === 'baseball' && inning === index + 1
        ? css.activeInning
        : null;

    return (
      <td className={activeBaseballSegmentClass} {...activeInningId}>
        {resultToDisplay}
      </td>
    );
  });

const renderTotalColumn = (tableData, league, firstTeamPos, secondTeamPos) => {
  const last_index = tableData.headers.length - 1;
  let start_index = last_index;

  if (league.sport_name === 'baseball') {
    start_index = last_index - 2;
  }

  const indexRange = Array.from(Array(last_index - start_index + 1), (v, i) => start_index + i);

  return (
    <table role="presentation" aria-hidden="true" className={css.lineScoreStaticTotalColumn}>
      <thead>
        <tr>
          {indexRange.map(index => (
            <th key={`static-total-header-${index}`} className={css.lineScoreColumnElement}>
              {tableData.headers[index]}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          {indexRange.map(index => (
            <td
              key={`${firstTeamPos}-total-result-${index}`}
              className={css.lineScoreColumnElement}
            >
              {tableData[firstTeamPos][index]}
            </td>
          ))}
        </tr>
        <tr>
          {indexRange.map(index => (
            <td
              key={`${secondTeamPos}-total-result-${index}`}
              className={css.lineScoreColumnElement}
            >
              {tableData[secondTeamPos][index]}
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );
};

class LineScoreCard extends React.PureComponent {
  componentDidMount() {
    setTimeout(() => {
      const container = this.lineScoreCardComponent;
      const teamColumnCells = Array.from(container.querySelectorAll('[class*="teamColumn"]'));
      const colWidth = teamColumnCells.reduce((acc, cell) => Math.max(acc, cell.clientWidth), 0);
      const scrollableScoreTable = document.getElementById('scrollableScoreTable');
      const { sport_name, event_status } = this.props.event;

      teamColumnCells.forEach(el => {
        // eslint-disable-next-line no-param-reassign
        el.clientwidth = colWidth;
      });

      container.style.paddingLeft = `${colWidth}px`;

      if (scrollableScoreTable && sport_name === 'baseball' && event_status === 'in_progress') {
        const activeInning = document.getElementById('activeInning');

        scrollableScoreTable.scrollTo({
          left: activeInning.offsetLeft / 2,
          behavior: 'smooth',
        });
      }
    }, 100);
  }

  render() {
    const { nhlShots, event } = this.props;
    const { box_score, home_team, away_team, league, sport_name, event_status } = event;
    const homeFirst = isHomeFirst(league);
    const lineScores = get(box_score, 'line_scores', {});
    const progress = get(box_score, 'line_scores', {});
    const teams = { away: away_team, home: home_team };
    const firstTeamPosition = homeFirst ? 'home' : 'away';
    const secondTeamPosition = homeFirst ? 'away' : 'home';
    const tableData = getTableData(lineScores, progress, box_score, league, nhlShots);
    const segmentDivision = get(box_score, 'progress.segment_division', '');
    const segment = get(box_score, 'progress.segment', 0);

    return (
      <div
        ref={element => {
          this.lineScoreCardComponent = element;
        }}
        className={cssContext(css.lineScoreContainer, {
          baseball: league.sport_name === 'baseball',
        })}
      >
        <div className={css.scrollableScoreTable} id="scrollableScoreTable">
          <table>
            <caption className="sr-only">Line Score</caption>
            <thead>{renderHeader(tableData.headers, league)}</thead>
            <tbody>
              <tr>
                <th
                  scope="row"
                  className={css.teamColumn}
                  aria-label={teams[firstTeamPosition].full_name}
                >
                  <TeamChip team={teams[firstTeamPosition]} />
                </th>
                {renderTeamRecord(
                  firstTeamPosition,
                  progress,
                  tableData,
                  sport_name,
                  event_status,
                  segmentDivision === 'top',
                  segment
                )}
              </tr>
              <tr>
                <th
                  scope="row"
                  className={css.teamColumn}
                  aria-label={teams[secondTeamPosition].full_name}
                >
                  <TeamChip team={teams[secondTeamPosition]} />
                </th>
                {renderTeamRecord(
                  secondTeamPosition,
                  progress,
                  tableData,
                  sport_name,
                  event_status,
                  segmentDivision === 'bottom',
                  segment
                )}
              </tr>
            </tbody>
          </table>
        </div>
        {renderTotalColumn(tableData, league, firstTeamPosition, secondTeamPosition)}
      </div>
    );
  }
}

LineScoreCard.propTypes = {
  event: PropTypes.shape({
    home_team: PropTypes.object,
    away_team: PropTypes.object,
    box_score: PropTypes.object,
    league: PropTypes.object,
    sport_name: PropTypes.string,
    event_status: PropTypes.string,
  }).isRequired,
  nhlShots: PropTypes.bool,
};

LineScoreCard.defaultProps = {
  nhlShots: false,
};

LineScoreCard.displayName = 'LineScoreCard';

export default StyledComponent(LineScoreCard, [css]);
